/*
 * RARA comparison skin - progressive enhancement for comparison tables.
 *  1. Auto-highlight: on a product page, a stock comparison table left on
 *     "Highlighted column = 0" highlights the column whose header matches the
 *     product (tag `compare:<Header>` wins, else a unique word match with the
 *     product title, e.g. "Date" in "RARA Double Date Bundle").
 *  2. Swipe nav: arrows + progress bar under any table that overflows.
 * Works without this file (the table just scrolls). Re-runs in the theme editor.
 */
(() => {
  const SCROLLERS =
    '.comparison-table:not(.rara-ct-off) .comparison-table__table-wrapper, .rara-compare .rara-compare__scroll';
  const product = window.RARA_COMPARISON_PRODUCT || null;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // combining accents (U+0300-U+036F), built without escapes so the file stays plain ASCII
  const DIACRITICS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
  const norm = (s) =>
    String(s || '').toLowerCase().normalize('NFD').replace(DIACRITICS, '').replace(/[^a-z0-9]+/g, ' ').trim();

  function autoHighlight(table) {
    if (!product || !table.classList.contains('highlight-column-0')) return;
    if (table.closest('.rara-ct-noauto')) return;
    const head = table.rows[0];
    if (!head || head.cells.length < 3) return;
    const names = [...head.cells].map((c) => norm(c.textContent));

    let index = -1;
    const tag = (product.tags || []).map(norm).find((t) => t.startsWith('compare '));
    if (tag) index = names.findIndex((n, i) => i > 0 && n === tag.slice(8));

    if (index < 1) {
      const title = ` ${norm(product.title)} `;
      const hits = names.map((n, i) =>
        i > 0 && n.split(' ').some((w) => w.length >= 3 && title.includes(` ${w} `))
      );
      if (hits.filter(Boolean).length === 1) index = hits.indexOf(true);
    }
    if (index < 1) return;

    table.classList.replace('highlight-column-0', `highlight-column-${index + 1}`);
    table.classList.add('rct-auto');
  }

  const arrow = (dir) =>
    `<svg viewBox="0 0 22 16" fill="none" aria-hidden="true"><path d="${
      dir < 0 ? 'M21 8H1.5M8 1.5 1.5 8 8 14.5' : 'M1 8h19.5M14 1.5 20.5 8 14 14.5'
    }" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  function enhance(scroller) {
    if (scroller.dataset.rct) return;
    const table = scroller.querySelector('table');
    if (!table) return;
    scroller.dataset.rct = '1';

    autoHighlight(table);

    const root = scroller.closest('.comparison-table, .rara-compare');
    const title = root && root.querySelector('h2, h3, .rara-compare__title');
    scroller.classList.add('rct-scroller');
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-label', `${title ? title.textContent.trim() : 'Comparison'} (scrolls sideways)`);
    if (!scroller.hasAttribute('tabindex')) scroller.tabIndex = 0;

    const nav = document.createElement('div');
    nav.className = 'rct-nav';
    nav.hidden = true;
    nav.innerHTML =
      `<button type="button" class="rct-nav__btn" data-dir="-1" aria-label="Previous column">${arrow(-1)}</button>` +
      '<div class="rct-nav__track" aria-hidden="true"><span class="rct-nav__bar"></span></div>' +
      `<button type="button" class="rct-nav__btn" data-dir="1" aria-label="Next column">${arrow(1)}</button>`;
    scroller.after(nav);

    const [prev, next] = nav.querySelectorAll('button');
    const bar = nav.querySelector('.rct-nav__bar');

    const step = () => {
      const cell = table.rows[0] && table.rows[0].cells[1];
      return cell ? cell.getBoundingClientRect().width : scroller.clientWidth * 0.6;
    };
    nav.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      scroller.scrollBy({ left: Number(btn.dataset.dir) * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    });

    const update = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      const overflow = max > 2;
      nav.hidden = !overflow;
      scroller.classList.toggle('rct-scrolled', overflow && scroller.scrollLeft > 2);
      if (!overflow) return;
      const x = Math.min(max, Math.max(0, scroller.scrollLeft));
      bar.style.width = `${((scroller.clientWidth + x) / scroller.scrollWidth) * 100}%`;
      prev.disabled = x <= 2;
      next.disabled = x >= max - 2;
    };
    scroller.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(scroller);
    else window.addEventListener('resize', update);
    update();
  }

  const init = (scope = document) => scope.querySelectorAll(SCROLLERS).forEach(enhance);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
